import Container from "../../../components/element/container";
import { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Icon from "../../../components/element/icon";
import formatNumber from "../../../helpers/format.number";

const DOWN_PAYMENT_OPTIONS = [10, 20, 30, 40, 50];
const TENURE_OPTIONS = [9, 8, 7, 6, 5];
const DEFAULT_INTEREST_RATE = 3.8;

const money = (value) =>
  value === null || value === undefined || value === "" || Number.isNaN(value)
    ? "-"
    : `RM ${formatNumber(Number(value))}`;

const steps = [
  {
    icon: "solar:card-2-linear",
    caption: "Fully Refundable",
  },
  {
    icon: "solar:clipboard-text-linear",
    title: "Sign Agreement",
    caption: "Check & Agree",
  },
  {
    icon: "solar:buildings-2-linear",
    caption: "We Handle Everything",
  },
];

export default function FinancingSection({
  carPrice = 0,
  reservationFee = 5000,
  handlingFee = 3000,
  roadTax,
  insurance,
}) {
  const { t } = useTranslation();
  const rateInputRef = useRef(null);
  const previousRateRef = useRef(DEFAULT_INTEREST_RATE);
  const [mode, setMode] = useState("cash");
  const [interestRate, setInterestRate] = useState(DEFAULT_INTEREST_RATE);
  const [editingRate, setEditingRate] = useState(false);
  const [downPaymentPercent, setDownPaymentPercent] = useState(
    DOWN_PAYMENT_OPTIONS[0],
  );
  const [tenure, setTenure] = useState(TENURE_OPTIONS[0]);

  const vehiclePrice = Number(carPrice || 0);
  const fees =
    Number(handlingFee || 0) +
    Number(roadTax || 0) +
    Number(insurance || 0);
  // The reservation deposit is part of the purchase price, not an extra fee.
  const totalPrice = vehiclePrice + fees;
  // The percentage applies to the vehicle alone; the fees are payable in full
  // up front, so they are added on top to make the upfront payment.
  const downPayment = (vehiclePrice * downPaymentPercent) / 100;
  const upfrontPayment = downPayment + fees;
  const isLoan = mode === "loan";
  // The reservation fee is already paid at step 1, so the balance left to settle
  // is whatever the chosen mode asks for up front, minus that deposit.
  const payBalance = Math.max(
    0,
    (isLoan ? upfrontPayment : totalPrice) - Number(reservationFee || 0),
  );

  // Malaysian hire purchase uses a flat rate: interest is charged on the full
  // financed amount for every year of the tenure.
  const monthlyInstalment = useMemo(() => {
    const principal = Math.max(0, vehiclePrice - downPayment);
    const interest = principal * (Number(interestRate) / 100) * tenure;

    return tenure > 0 ? (principal + interest) / (tenure * 12) : 0;
  }, [downPayment, interestRate, tenure, vehiclePrice]);

  const stepValues = [
    Number(reservationFee) > 0
      ? `${money(reservationFee)} ${t("Deposit")}`
      : t("Deposit"),
    t("Sign Agreement"),
    `${t("Pay Balance")} ${money(payBalance)}`,
  ];

  const priceDetails = [
    ["Car Price", money(carPrice)],
    ...(Number(reservationFee) > 0
      ? [["Reservation Fees", money(reservationFee)]]
      : []),
    ["Handling Fees", handlingFee ? money(handlingFee) : "-"],
    ["Est. One Year Road Tax", roadTax ? money(roadTax) : "-"],
    ["Est Insurance", insurance ? money(insurance) : "-"],
  ];

  const commitRate = () => {
    const next = Number(rateInputRef.current?.value);

    setInterestRate(
      Number.isFinite(next) && next > 0 && next <= 100
        ? Number(next.toFixed(2))
        : interestRate,
    );
    setEditingRate(false);
  };

  return (
    <section className="financing-section" aria-label="Financing">
      <header className="financing-heading">
        <Icon icon="solar:document-text-linear" />
        <h4>{t("Financing")}</h4>
        <span />
      </header>

      <Container tag="div" className="financing-steps">
        {steps.map((step, index) => (
          <article className="financing-step" key={step.icon}>
            <Container tag="div" className="financing-step-mark">
              <span>{index + 1}</span>
              <Icon icon={step.icon} />
            </Container>
            <p className="financing-step-title">{stepValues[index]}</p>
            <p className="financing-step-caption">{t(step.caption)}</p>
          </article>
        ))}
      </Container>

      <Container tag="div" className="financing-modes">
        <button
          type="button"
          className={`financing-mode ${isLoan ? "" : "active"}`}
          aria-pressed={!isLoan}
          onClick={() => setMode("cash")}
        >
          <strong>{t("CASH BUY")}</strong>
          <span>
            {t("Price")} {money(totalPrice)}
          </span>
        </button>
        <button
          type="button"
          className={`financing-mode ${isLoan ? "active" : ""}`}
          aria-pressed={isLoan}
          onClick={() => setMode("loan")}
        >
          <strong>{t("APPLY LOAN")}</strong>
          <span>
            {t("Upfront Payment")} {money(upfrontPayment)}
          </span>
        </button>
      </Container>

      {isLoan && (
        <Container tag="div" className="financing-loan">
          <Container tag="div" className="financing-row">
            <h5>{t("Interest Rate")}</h5>
            {editingRate ? (
              <Container tag="div" className="financing-rate-edit">
                <input
                  ref={rateInputRef}
                  type="number"
                  aria-label="Interest rate percentage"
                  step="0.1"
                  min="0.1"
                  max="100"
                  defaultValue={interestRate}
                  autoFocus
                  onChange={(event) => {
                    const next = event.target.valueAsNumber;
                    if (Number.isFinite(next) && next > 0 && next <= 100) {
                      setInterestRate(next);
                    }
                  }}
                  onBlur={commitRate}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") commitRate();
                    if (event.key === "Escape") {
                      setInterestRate(previousRateRef.current);
                      setEditingRate(false);
                    }
                  }}
                />
                <span>%</span>
              </Container>
            ) : (
              <button
                type="button"
                className="financing-rate"
                onClick={() => {
                  previousRateRef.current = interestRate;
                  setEditingRate(true);
                }}
              >
                {interestRate}%
                <Icon icon="solar:pen-linear" />
              </button>
            )}
          </Container>

          <Container tag="div" className="financing-row">
            <h5>{t("Down Payment %")}</h5>
            <strong>{money(downPayment)}</strong>
          </Container>
          <Container tag="div" className="financing-options">
            {DOWN_PAYMENT_OPTIONS.map((option) => (
              <button
                type="button"
                className={`financing-option ${
                  option === downPaymentPercent ? "active" : ""
                }`}
                key={option}
                onClick={() => setDownPaymentPercent(option)}
              >
                {option}%
              </button>
            ))}
          </Container>

          <Container tag="div" className="financing-row">
            <h5>{t("Monthly Installment")}</h5>
            <strong>{money(monthlyInstalment)}</strong>
          </Container>
          <Container tag="div" className="financing-options">
            {TENURE_OPTIONS.map((option) => (
              <button
                type="button"
                className={`financing-option ${
                  option === tenure ? "active" : ""
                }`}
                key={option}
                onClick={() => setTenure(option)}
              >
                {option} Year
              </button>
            ))}
          </Container>
        </Container>
      )}

      <Container tag="div" className="financing-prices">
        <h5>{t("Price Details")}</h5>
        {priceDetails.map(([label, value]) => (
          <Container tag="div" className="financing-price-row" key={label}>
            <span>{t(label)}</span>
            <strong>{value}</strong>
          </Container>
        ))}
      </Container>
    </section>
  );
}
